import React, { useState } from 'react';
import { X, Tag } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { cn } from '@/lib/utils';
import { ProjectContact } from '@/types';
import { projectService, toContactInput } from '@/services/projectService';
import ProjectContactsEditor from '@/components/ProjectContactsEditor';

interface NewProjectModalProps {
  open: boolean;
  onClose: () => void;
  preselectedProjectId?: string;
  refreshProjects?: () => void;
}

const availableTags = [
  'מגורים', 'משרדים', 'מחסנים', 'מסחר', 'הריסה', 'בניה', 'שיפוץ', 'חידוש', 'פרטי', 'ציבורי', 'רב-קומות', 'בית פרטי'
];

export default function NewProjectModal({ open, onClose, preselectedProjectId, refreshProjects }: NewProjectModalProps) {
  const { projects } = useApp();
  
  const [projectAddress, setProjectAddress] = useState('');
  const [projectStartDate, setProjectStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [projectDueDate, setProjectDueDate] = useState('');
  const [projectClientName, setProjectClientName] = useState('');
  const [contacts, setContacts] = useState<ProjectContact[]>([]);
  const [showContactErrors, setShowContactErrors] = useState(false);
  const [description, setDescription] = useState('');
  const [title, setTitle] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [validations, setValidations] = useState<{[key: string]: boolean}>({
    title: true,
    projectAddress: true,
    projectClientName: true,
    projectStartDate: true
  });

  const resetForm = () => {
    setProjectAddress('');
    setProjectStartDate(new Date().toISOString().split('T')[0]);
    setProjectClientName('');
    setContacts([]);
    setShowContactErrors(false);
    setDescription('');
    setSelectedTags([]);
    setValidations({
      title: true,
      projectAddress: true,
      projectClientName: true,
      projectStartDate: true
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const toggleTag = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleSubmit = async () => {

    // Basic validations
    const newValidations = {
      title: title.trim().length > 0,
      projectAddress: projectAddress.trim().length > 0,
      projectClientName: projectClientName.trim().length > 0,
      projectStartDate: projectStartDate.length > 0
    };

    setValidations(newValidations);

    if (!newValidations.title || !newValidations.projectAddress || !newValidations.projectClientName || !newValidations.projectStartDate) {
      return;
    }

    const hasInvalidContact = contacts.some(c => !c.name.trim() || !c.phone.trim());
    if (hasInvalidContact) {
      setShowContactErrors(true);
      return;
    }
    setShowContactErrors(false);

    const payload: Record<string, unknown> = {
      name: title,
      clientName: projectClientName,
      status: 'planning',
      startDate: projectStartDate,
      tags: selectedTags,
      address: projectAddress,
    };
    if (contacts.length > 0) {
      payload.contacts = contacts.map(toContactInput);
      payload.Phone = contacts[0].phone;
    }

    try {
      await projectService.createProject(payload);
      handleClose();
      refreshProjects();
    } catch (error) {

    }
  };

  const onChangeFields = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;    
    switch (name) {
      case 'title':
        setTitle(value);
        setValidations((prev) => ({ ...prev, title: value.trim().length > 0 }));
        break;
      case 'projectAddress':
        setProjectAddress(value);
        setValidations((prev) => ({ ...prev, projectAddress: value.trim().length > 0 }));
        break;
      case 'projectClientName':
        setProjectClientName(value);
        setValidations((prev) => ({ ...prev, projectClientName: value.trim().length > 0 }));
        break;
      case 'projectStartDate':
        setProjectStartDate(value);
        setValidations((prev) => ({ ...prev, projectStartDate: value.length > 0 }));
        break;
      case 'description':
        setDescription(value);
        break;
      case 'projectDueDate':
        setProjectDueDate(value);
        break;
      default:
        break;
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-foreground/50 z-50 flex items-end md:items-center justify-center">
      <div className="bg-card w-full md:w-[500px] md:max-h-[90vh] rounded-t-2xl md:rounded-2xl overflow-hidden animate-slide-up shadow-soft">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-lg font-bold">פרוייקט חדש</h2>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-secondary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 max-h-[70vh] overflow-y-auto">         
            <div className="space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium mb-3">שם הפרוייקט</label>
                <input
                  type="text"
                  value={title}
                  name='title'
                  onChange={onChangeFields}
                  // onChange={(e) => setTitle(e.target.value)}
                  placeholder="שם הפרויקט..."
                  className={`w-full px-4 py-3 rounded-xl border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring ${!validations.title ? 'border-red-500' : 'border-gray-300'}`}
                  required
                />
              </div>
              {/* Address */}
              <div>
                <label className="block text-sm font-medium mb-3">כתובת</label>
                <input
                  type="text"
                  name='projectAddress'
                  value={projectAddress}
                  onChange={onChangeFields}
                  placeholder="כתובת הפרויקט..."
                  className={`w-full px-4 py-3 rounded-xl border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring ${!validations.projectAddress ? 'border-red-500' : 'border-gray-300'}`}
                  required
                />
              </div>
              {/* Date */}
              <div>
                <label className="block text-sm font-medium mb-3">תאריך התחלה</label>
                <input
                  type="date"
                  name='projectStartDate'
                  value={projectStartDate}
                  onChange={onChangeFields}
                  className={`w-full px-4 py-3 rounded-xl border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring ${!validations.projectStartDate ? 'border-red-500' : 'border-gray-300'}`}
                  required
                />
              </div>
               {/* Customer name */}
              <div>
                <label className="block text-sm font-medium mb-3">שם לקוח</label>
                <input
                  type="text"
                  name='projectClientName'
                  value={projectClientName}
                  onChange={onChangeFields}
                  placeholder="שם הלקוח..."
                  className={`w-full px-4 py-3 rounded-xl border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring ${!validations.projectClientName ? 'border-red-500' : 'border-gray-300'}`}
                  required
                />
              </div>
              {/* Contacts */}
              <div>
                <label className="block text-sm font-medium mb-3">אנשי קשר</label>
                <ProjectContactsEditor
                  contacts={contacts}
                  onChange={setContacts}
                  showErrors={showContactErrors}
                />
              </div>
              {/* Date */}
              <div>
                <label className="block text-sm font-medium mb-3">תאריך סיום משוער</label>
                <input
                  type="date"
                  name='projectDueDate'
                  value={projectDueDate}
                  onChange={onChangeFields}
                  className="w-full px-4 py-3 rounded-xl border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              {/* Tags */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium mb-3">
                  <Tag className="w-4 h-4" />
                  תגיות
                </label>
                <div className="flex flex-wrap gap-2">
                  {availableTags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => toggleTag(tag)}
                      className={cn(
                        "px-3 py-1.5 rounded-full text-sm font-medium transition-all",
                        selectedTags.includes(tag)
                          ? "bg-primary text-primary-foreground"
                          : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                      )}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium mb-3">תיאור כללי של הפרוייקט</label>
                <textarea
                  value={description}
                  name='description'
                  onChange={onChangeFields}
                  placeholder="תיאור..."
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl border border-input bg-background resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-5 border-t border-border">
          <button
            onClick={handleSubmit}
            className="flex-1 px-6 py-3 rounded-xl gradient-primary text-primary-foreground font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
          >
            צור פרוייקט
          </button>
        </div>
      </div>
    </div>
  );
}
